import type { CapabilityPackDefinition, ResolvedPack } from '@/types/institution-experience';

/**
 * What actually changes when one pack is toggled — computed, not asserted.
 *
 * A pack's card says what the pack CONTAINS, but "turn off Library" does not remove
 * everything Library contains: a capability two active packs both carry survives the
 * loss of one of them. The confirm dialog must name the true delta or it teaches
 * people that toggles are scarier (or safer) than they are, so the diff is set math
 * over the union of the OTHER active packs — the same computation core performs when
 * it rebuilds the resolved set, run here ahead of the write so the admin sees the
 * consequence before committing to it.
 *
 * Pure function, tested in test/pack-diff.test.ts. The catalogue is the source of
 * each pack's contents; the resolved packDetail is the source of what is active.
 */

export interface PackDiff {
  /** Capabilities the institution would gain / lose, in catalogue order. */
  capabilitiesAdded: string[];
  capabilitiesRemoved: string[];
  modulesAdded: string[];
  modulesRemoved: string[];
  /** True when the toggle changes nothing — every gain/loss is covered by another active pack. */
  isNoop: boolean;
}

function unionOf(
  catalogue: CapabilityPackDefinition[],
  activePacks: ReadonlySet<string>,
  field: 'capabilities' | 'modules',
): Set<string> {
  const union = new Set<string>();
  for (const definition of catalogue) {
    if (!activePacks.has(definition.pack)) continue;
    for (const entry of definition[field]) union.add(entry);
  }
  return union;
}

/**
 * The delta of toggling `pack` to `enable`, given which packs are active right now.
 *
 * `activePacks` comes from the resolved set's packDetail (active === true), which
 * already folds defaults and overrides together — this function does not need to
 * know WHY a pack is on, only THAT it is.
 */
export function computePackDiff(
  catalogue: CapabilityPackDefinition[],
  packDetail: ResolvedPack[],
  pack: string,
  enable: boolean,
): PackDiff {
  const before = new Set(packDetail.filter((entry) => entry.active).map((entry) => entry.pack));
  const after = new Set(before);
  if (enable) after.add(pack);
  else after.delete(pack);

  const diff = (field: 'capabilities' | 'modules'): { added: string[]; removed: string[] } => {
    const beforeUnion = unionOf(catalogue, before, field);
    const afterUnion = unionOf(catalogue, after, field);
    // Catalogue order, not Set insertion order, so the dialog lists changes in the
    // same order the cards present them.
    const ordered: string[] = [];
    for (const definition of catalogue) {
      for (const entry of definition[field]) {
        if (!ordered.includes(entry)) ordered.push(entry);
      }
    }
    return {
      added: ordered.filter((entry) => afterUnion.has(entry) && !beforeUnion.has(entry)),
      removed: ordered.filter((entry) => beforeUnion.has(entry) && !afterUnion.has(entry)),
    };
  };

  const capabilities = diff('capabilities');
  const modules = diff('modules');
  return {
    capabilitiesAdded: capabilities.added,
    capabilitiesRemoved: capabilities.removed,
    modulesAdded: modules.added,
    modulesRemoved: modules.removed,
    isNoop:
      capabilities.added.length === 0 &&
      capabilities.removed.length === 0 &&
      modules.added.length === 0 &&
      modules.removed.length === 0,
  };
}

/**
 * Whether a pack's state departs from what the institution's type would give it.
 *
 * ADDED and REMOVED are the departures an auditor scans for; CONFIRMED matches the
 * default and DEFAULT is the default, so neither is a deviation even though
 * CONFIRMED carries an override row.
 */
export function isDeparture(source: ResolvedPack['source']): boolean {
  return source === 'ADDED' || source === 'REMOVED';
}
